/**
 * Singurul loc din web care vorbeste cu reteaua.
 *
 * Web-ul e vitrina: nu decide nimic, doar cere si afiseaza. De aceea aici nu
 * exista niciun calcul de pret, de acoperire sau de ETA — doar transport,
 * anteturi de contract si traducerea erorilor in ceva ce se poate arata.
 *
 * Doua reguli care nu se incalca:
 *  - un raspuns care nu e cel asteptat devine EROARE VIZIBILA, nu date goale.
 *    404 pe o ruta inseamna "serverul nu are ruta", nu "nu exista rezultate";
 *  - mesajul de eroare al serverului se afiseaza asa cum vine (e in romana si
 *    stie ce s-a intamplat). Ce adaugam noi nu presupune nicio cauza.
 */

import {
  CORE_CONTRACT_VERSION,
  CORE_ERROR_CODES,
  HEADER_CORE_CONTRACT,
  HEADER_PLATFORM_INSTANCE,
  HEADER_REQUEST_ID,
  PUBLIC_ROUTES,
  publicPath,
  type ApiErrorBody,
  type BootstrapResponse,
  type CheckoutRequest,
  type CheckoutResponse,
  type CoreErrorCode,
  type GeocodeRequest,
  type GeocodeResponse,
  type OrderTrackingDto,
  type QuoteRequest,
  type QuoteResponse,
  type ReservationCreateRequest,
  type ReservationCreateResponse,
  type ReservationSlotsRequest,
  type ReservationSlotsResponse,
  type ReservationVendorsResponse,
  type ReverseGeocodeRequest,
  type StorefrontAddressResponse,
  type StorefrontCoverageResponse,
  type StorefrontDto,
  type PickupPointDto,
  type PickupPointsResponse,
} from "@deliveryapp/shared";
import { dispatcherToken } from "../dispatcher/dispatcher-session";

export type { GeocodeCandidate, GeocodeRequest, GeocodeResponse } from "@deliveryapp/shared";

/** Coduri produse de client, nu de server. Nu se suprapun cu cele din contract. */
export const CLIENT_ERROR_CODES = {
  NETWORK: "NETWORK",
  TIMEOUT: "TIMEOUT",
  MALFORMED_RESPONSE: "MALFORMED_RESPONSE",
  /** 502/503/504 sau 520-527 (Cloudflare) cu pagina proxy-ului din fata in loc de o eroare de core. */
  GATEWAY_UNAVAILABLE: "GATEWAY_UNAVAILABLE",
} as const;

export type ClientErrorCode = (typeof CLIENT_ERROR_CODES)[keyof typeof CLIENT_ERROR_CODES];

export class ApiError extends Error {
  readonly status: number;
  readonly code: CoreErrorCode | ClientErrorCode | string;
  readonly requestId: string | undefined;
  readonly details: Record<string, unknown> | undefined;

  constructor(init: {
    status: number;
    code: CoreErrorCode | ClientErrorCode | string;
    message: string;
    requestId?: string;
    details?: Record<string, unknown>;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.details = init.details;
  }

  /** Reincercarea are sens doar cand cauza e transportul sau serverul, nu cererea. */
  get retryable(): boolean {
    if (this.status === 0 || this.status >= 500) return true;
    return this.code === CLIENT_ERROR_CODES.NETWORK || this.code === CLIENT_ERROR_CODES.TIMEOUT;
  }

  /** ADR-002: web-ul e mai nou sau mai vechi decat core-ul; singura iesire e reincarcarea. */
  get requiresReload(): boolean {
    return this.status === 426 || this.code === CORE_ERROR_CODES.CONTRACT_UNSUPPORTED;
  }
}

// ---------------------------------------------------------------------------
// Identitatea pietei
// ---------------------------------------------------------------------------

const MARKET_QUERY_PARAM = "market";

/**
 * Imaginea web e identica pentru toate pietele, deci identitatea pietei NU
 * poate fi coapta in bundle. Singurul lucru pe care il stie despre sine la
 * runtime e domeniul pe care a fost servita — si exact acela e pinuit in
 * `markets.domain` in core.
 *
 * Ordinea: parametru explicit (link de test), variabila de dezvoltare, domeniu.
 * Pe `localhost` nu exista domeniu de piata, deci acolo lipsa variabilei e o
 * eroare de configurare a mediului de dezvoltare, semnalata ca atare.
 */
export function resolveMarketKey(): string {
  const fromQuery = new URLSearchParams(window.location.search).get(MARKET_QUERY_PARAM);
  if (fromQuery !== null && fromQuery.trim().length > 0) return fromQuery.trim();

  const fromEnv = import.meta.env.VITE_MARKET_KEY;
  const hostname = window.location.hostname;
  const isLocal = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
  if (typeof fromEnv === "string" && fromEnv.trim().length > 0 && isLocal) {
    return fromEnv.trim();
  }
  if (isLocal) {
    throw new Error(
      "Aplicatia ruleaza pe localhost, unde domeniul nu identifica nicio piata. " +
        "Seteaza VITE_MARKET_KEY sau adauga ?market=<slug> in adresa.",
    );
  }
  return hostname;
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");

const DEFAULT_TIMEOUT_MS = 15_000;
/** Commit-ul trece prin core pana in POS-ul vendorului; 15 s nu ajung. */
const CHECKOUT_TIMEOUT_MS = 30_000;

interface RequestOptions {
  method?: "GET" | "POST";
  body?: unknown;
  marketKey: string;
  timeoutMs?: number;
  signal?: AbortSignal;
  /**
   * Verificarea de forma a raspunsului reusit. Un 200 caruia ii lipseste
   * structura asteptata NU are voie sa ajunga la pagina: acolo devine "meniul
   * este gol" sau "comanda nu are produse", adica un defect de server citit de
   * client ca stare normala.
   */
  expect?: (value: unknown) => boolean;
  /** Token de dispecer explicit (verificarea sesiunii abia primite din POS). */
  dispatcherToken?: string | null;
}

/**
 * Detaliul tehnic (ruta, statusul, codul) nu se afiseaza clientului — el nu
 * poate face nimic cu el, iar ecranul devine un raport de eroare. Se scrie in
 * consola, unde suportul si dezvoltatorul chiar il pot citi, si ramane pe
 * obiectul `ApiError` pentru orice consumator programatic. Pe ecran ramane doar
 * referinta cererii, cu care suportul gaseste inregistrarea in core.
 */
function logTechnicalDetail(detail: Record<string, unknown>): void {
  // eslint-disable-next-line no-console
  console.warn("[deliveryapp] cerere esuata", detail);
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { code?: unknown }).code === "string" &&
    typeof (value as { message?: unknown }).message === "string"
  );
}

async function request<T>(path: string, options: RequestOptions): Promise<T> {
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  const externalAbort = (): void => controller.abort();
  options.signal?.addEventListener("abort", externalAbort);

  const headers: Record<string, string> = {
    Accept: "application/json",
    [HEADER_CORE_CONTRACT]: String(CORE_CONTRACT_VERSION),
    [HEADER_PLATFORM_INSTANCE]: options.marketKey,
    [HEADER_REQUEST_ID]: crypto.randomUUID(),
  };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  // Modul dispecer: comanda luata la telefon trece prin aceleasi rute publice,
  // cu sesiunea dispecerului ca sa fie marcata (si sa poata citi clientul).
  const dispatcherBearer = options.dispatcherToken ?? dispatcherToken(options.marketKey);
  if (dispatcherBearer !== null) {
    headers.Authorization = `Bearer ${dispatcherBearer}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: options.method ?? "GET",
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: controller.signal,
      credentials: "omit",
    });
  } catch (error: unknown) {
    if (controller.signal.aborted && options.signal?.aborted !== true) {
      throw new ApiError({
        status: 0,
        code: CLIENT_ERROR_CODES.TIMEOUT,
        message: `Serverul nu a răspuns în ${Math.round(timeoutMs / 1000)} secunde.`,
      });
    }
    throw new ApiError({
      status: 0,
      code: CLIENT_ERROR_CODES.NETWORK,
      message: "Cererea nu a ajuns la server. Verifică legătura la internet și încearcă din nou.",
      details: { cause: error instanceof Error ? error.message : String(error) },
    });
  } finally {
    window.clearTimeout(timer);
    options.signal?.removeEventListener("abort", externalAbort);
  }

  const raw = await response.text();
  let parsed: unknown = undefined;
  if (raw.length > 0) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = undefined;
    }
  }

  if (!response.ok) {
    if (isApiErrorBody(parsed)) {
      logTechnicalDetail({ path, status: response.status, code: parsed.code, requestId: parsed.requestId });
      throw new ApiError({
        status: response.status,
        code: parsed.code,
        message: parsed.message,
        requestId: parsed.requestId,
        details: parsed.details,
      });
    }
    // Corpul nu respecta contractul de eroare. Nu se numeste niciun vinovat:
    // singurul lucru stiut e ca raspunsul nu are forma unei erori de core.
    // Un 502/503/504 cu pagina HTML e pagina proxy-ului din fata (Cloudflare
    // inlocuieste corpul unui 502 al core-ului), iar 520-527 sunt codurile
    // proprii ale Cloudflare (origine oprita la redeploy, timeout): pentru client
    // inseamna „nu raspunde acum", nu un format pe care trebuie sa-l inteleaga.
    logTechnicalDetail({ path, status: response.status, bodyPreview: raw.slice(0, 200) });
    const gateway =
      response.status === 502 ||
      response.status === 503 ||
      response.status === 504 ||
      (response.status >= 520 && response.status <= 527);
    throw new ApiError({
      status: response.status,
      code: gateway ? CLIENT_ERROR_CODES.GATEWAY_UNAVAILABLE : CLIENT_ERROR_CODES.MALFORMED_RESPONSE,
      message: gateway
        ? "Serviciul nu a răspuns momentan. Încearcă din nou în câteva secunde."
        : "Serverul a răspuns într-un format pe care aplicația nu îl poate interpreta.",
      details: { path, bodyPreview: raw.slice(0, 200) },
    });
  }

  if (parsed === undefined) {
    logTechnicalDetail({ path, status: response.status, bodyPreview: raw.slice(0, 200) });
    throw new ApiError({
      status: response.status,
      code: CLIENT_ERROR_CODES.MALFORMED_RESPONSE,
      message: "Răspunsul serverului nu este în formatul așteptat de aplicație.",
      details: { path, bodyPreview: raw.slice(0, 200) },
    });
  }

  if (options.expect !== undefined && !options.expect(parsed)) {
    // Un 200 fara structura ceruta se opreste AICI. Daca ar trece mai departe,
    // pagina l-ar reda ca stare goala legitima ("meniul este gol"), iar defectul
    // n-ar fi raportat de nimeni.
    logTechnicalDetail({ path, status: response.status, reason: "forma raspunsului nu corespunde" });
    throw new ApiError({
      status: response.status,
      code: CLIENT_ERROR_CODES.MALFORMED_RESPONSE,
      message: "Răspunsul serverului nu conține informația pe care o așteaptă aplicația.",
      details: { path },
    });
  }

  return parsed as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

// ---------------------------------------------------------------------------
// Rutele publice ale core-ului
// ---------------------------------------------------------------------------

/**
 * Nicio cale nu se mai scrie aici. `publicPath` din contract e aceeasi sursa din
 * care core-ul isi inregistreaza rutele, deci o divergenta de nume devine eroare
 * de compilare, nu 404 la prima comanda (defect A1/G1).
 */

export function fetchBootstrap(marketKey: string, signal?: AbortSignal): Promise<BootstrapResponse> {
  return request<BootstrapResponse>(publicPath.bootstrap(marketKey), {
    method: PUBLIC_ROUTES.bootstrap.method,
    marketKey,
    signal,
    expect: (value) => isRecord(value) && isRecord(value.config) && Array.isArray(value.storefronts),
  });
}

export function fetchStorefront(
  marketKey: string,
  slug: string,
  signal?: AbortSignal,
  location?: { zoneId: string; lat: number; lng: number } | { pickupVendorId: string } | null,
): Promise<StorefrontDto> {
  const basePath = publicPath.storefront(slug);
  const path =
    location === undefined || location === null
      ? basePath
      : "pickupVendorId" in location
        ? `${basePath}?ridicare=${encodeURIComponent(location.pickupVendorId)}`
        : `${basePath}?zonaId=${encodeURIComponent(location.zoneId)}&lat=${encodeURIComponent(location.lat)}` +
          `&lng=${encodeURIComponent(location.lng)}`;
  return request<StorefrontDto>(path, {
    method: PUBLIC_ROUTES.storefront.method,
    marketKey,
    signal,
    // Fara verificarea asta, un 200 fara `menus` ajunge pe ecran ca "Meniul este
    // gol" — clientul crede ca restaurantul n-a publicat nimic, iar noi nu aflam.
    expect: (value) => isRecord(value) && Array.isArray(value.menus) && Array.isArray(value.zones),
  });
}

/**
 * Locatiile din care clientul poate ridica singur comanda. Un core mai vechi nu
 * are ruta (404): asta inseamna „fara ridicare", nu o eroare de aratat.
 */
export async function fetchPickupPoints(marketKey: string, slug: string, signal?: AbortSignal): Promise<PickupPointDto[]> {
  try {
    const response = await request<PickupPointsResponse>(publicPath.storefrontPickupPoints(slug), {
      method: PUBLIC_ROUTES.storefrontPickupPoints.method,
      marketKey,
      signal,
      expect: (value) => isRecord(value) && Array.isArray(value.pickupPoints),
    });
    return response.pickupPoints;
  } catch (error: unknown) {
    if (error instanceof ApiError && error.status === 404) return [];
    throw error;
  }
}

/**
 * Previzualizarea vitrinei pentru o adresa, CU COSUL GOL.
 *
 * O cotatie fara produse nu are ce estima, iar core-ul o respinge cu „Cosul nu
 * contine niciun produs" — adica exact starea de la prima vizita. Ruta asta
 * exista tocmai pentru asta: intoarce zona, vanzatorul probabil si un ETA
 * orientativ, marcate `provizoriu`.
 */
export function checkStorefrontAddress(
  marketKey: string,
  slug: string,
  point: { lat: number; lng: number },
  signal?: AbortSignal,
): Promise<StorefrontAddressResponse> {
  return request<StorefrontAddressResponse>(publicPath.storefrontAddress(slug), {
    method: PUBLIC_ROUTES.storefrontAddress.method,
    body: point,
    marketKey,
    signal,
    expect: (value) => isRecord(value) && isRecord(value.seller) && isRecord(value.eta),
  });
}

/**
 * Acoperirea tuturor vitrinelor pentru adresa clientului, dintr-o cerere.
 *
 * Ruta e noua fata de primul contract: un core mai vechi raspunde 404
 * `ROUTE_NOT_FOUND`, iar apelantul trateaza asta ca „nu stiu" (cardurile raman
 * fara randul de acoperire), nu ca „nu livram" — de aceea nu se converteste
 * aici intr-o lista goala.
 */
export function fetchStorefrontCoverage(
  marketKey: string,
  point: { lat: number; lng: number },
  signal?: AbortSignal,
): Promise<StorefrontCoverageResponse> {
  return request<StorefrontCoverageResponse>(publicPath.storefrontCoverage(), {
    method: PUBLIC_ROUTES.storefrontCoverage.method,
    body: point,
    marketKey,
    signal,
    expect: (value) => isRecord(value) && Array.isArray(value.storefronts),
  });
}

export function requestQuote(
  marketKey: string,
  payload: QuoteRequest,
  signal?: AbortSignal,
): Promise<QuoteResponse> {
  return request<QuoteResponse>(publicPath.quote(), {
    method: PUBLIC_ROUTES.quote.method,
    body: payload,
    marketKey,
    signal,
    expect: (value) => isRecord(value) && isRecord(value.totals) && isRecord(value.eta),
  });
}

export function submitCheckout(
  marketKey: string,
  payload: CheckoutRequest,
  signal?: AbortSignal,
): Promise<CheckoutResponse> {
  return request<CheckoutResponse>(publicPath.checkout(), {
    method: PUBLIC_ROUTES.checkout.method,
    body: payload,
    marketKey,
    signal,
    timeoutMs: CHECKOUT_TIMEOUT_MS,
    // Codul comenzii e singurul lucru cu care clientul isi mai poate gasi
    // comanda dupa ce ecranul se schimba. Un raspuns fara el nu e o confirmare.
    expect: (value) =>
      isRecord(value) &&
      typeof value.platformOrderCode === "string" &&
      value.platformOrderCode.length > 0,
  });
}

// ---------------------------------------------------------------------------
// Dispeceratul telefonic (modul dispecer)
// ---------------------------------------------------------------------------

export interface DispatcherSessionInfo {
  marketSlug: string;
  marketName: string;
  operator: string;
  hubClientId: number;
}

export function verifyDispatcherSession(marketKey: string, token: string): Promise<DispatcherSessionInfo> {
  return request<DispatcherSessionInfo>("/api/dispecer/sesiune", {
    method: "POST",
    body: {},
    marketKey,
    dispatcherToken: token,
    expect: (value) => isRecord(value) && typeof value.operator === "string",
  });
}

export interface DispatcherRepeatLine {
  lineId: string;
  offerId: string | null;
  skuId: string;
  name: string;
  qty: number;
  unitPriceGross: number;
  notes: string | null;
  selectedOptions: Array<{ groupId: string; optionId: string; priceDelta: number }>;
  optionLabels: string[];
  repeatable: boolean;
}

export interface DispatcherCustomerMemory {
  phone: string;
  known: boolean;
  name: string | null;
  ordersCount: number;
  lastOrderAt: string | null;
  addresses: Array<{
    key: string;
    street: string;
    streetNumber: string | null;
    building: string | null;
    floor: string | null;
    apartment: string | null;
    doorCode: string | null;
    city: string;
    postalCode: string | null;
    lat: number | null;
    lng: number | null;
    notes: string | null;
    uses: number;
    lastUsedAt: string | null;
  }>;
  orders: Array<{
    platformOrderCode: string;
    status: string;
    placedAt: string | null;
    total: number;
    sellerName: string | null;
    storefrontSlug: string | null;
    channel: string;
    items: DispatcherRepeatLine[];
  }>;
  activeOrder: { platformOrderCode: string; status: string; sellerName: string | null } | null;
}

/** Clientul care suna, la nivelul pietei. POST: telefonul nu sta in URL. */
export function fetchDispatcherCustomer(marketKey: string, phone: string, signal?: AbortSignal): Promise<DispatcherCustomerMemory> {
  return request<DispatcherCustomerMemory>("/api/dispecer/client", {
    method: "POST",
    body: { phone },
    marketKey,
    signal,
    expect: (value) => isRecord(value) && Array.isArray(value.addresses) && Array.isArray(value.orders),
  });
}

/**
 * Urmarirea comenzii (defect G2).
 *
 * Metoda e POST si telefonul e obligatoriu — nu din stil, ci pentru ca in
 * spatele unui cod scurt si ghicibil stau numele, adresa si telefonul clientului.
 * Web-ul chema `GET /api/orders/:code`, o ruta care nu exista, si arata "Comanda
 * nu a putut fi gasita" pentru comenzi deja intrate in bucatarie.
 */
export function fetchOrderTracking(
  marketKey: string,
  platformOrderCode: string,
  phone: string,
  signal?: AbortSignal,
): Promise<OrderTrackingDto> {
  return request<OrderTrackingDto>(publicPath.orderTracking(platformOrderCode), {
    method: PUBLIC_ROUTES.orderTracking.method,
    body: { phone },
    marketKey,
    signal,
    expect: (value) =>
      isRecord(value) && typeof value.status === "string" && Array.isArray(value.timeline),
  });
}

// ---------------------------------------------------------------------------
// Rezervari de masa
// ---------------------------------------------------------------------------

/**
 * Restaurantele pietei care primesc rezervari online.
 *
 * Lista vine de la core, care o compune intreband POS-ul fiecarui vendor. NU se
 * configureaza in blocul din builder: un identificator de vendor scris intr-un
 * blob de config ar ramane in urma realitatii fara ca nimeni sa afle (ADR-011),
 * iar aici „realitatea" se schimba de la sine — un restaurant care isi opreste
 * rezervarile trebuie sa dispara din formular in aceeasi zi.
 */
export function fetchReservationVendors(
  marketKey: string,
  signal?: AbortSignal,
): Promise<ReservationVendorsResponse> {
  return request<ReservationVendorsResponse>(publicPath.reservationVendors(), {
    method: PUBLIC_ROUTES.reservationVendors.method,
    marketKey,
    signal,
    expect: (value) => isRecord(value) && Array.isArray(value.vendors),
  });
}

export function fetchReservationSlots(
  marketKey: string,
  vendorSlug: string,
  payload: ReservationSlotsRequest,
  signal?: AbortSignal,
): Promise<ReservationSlotsResponse> {
  return request<ReservationSlotsResponse>(publicPath.reservationSlots(vendorSlug), {
    method: PUBLIC_ROUTES.reservationSlots.method,
    body: payload,
    marketKey,
    signal,
    // Lista goala inseamna „nicio ora libera". Lipsa listei inseamna alt raspuns.
    expect: (value) => isRecord(value) && Array.isArray(value.slots),
  });
}

export function createReservation(
  marketKey: string,
  vendorSlug: string,
  payload: ReservationCreateRequest,
  signal?: AbortSignal,
): Promise<ReservationCreateResponse> {
  return request<ReservationCreateResponse>(publicPath.reservationCreate(vendorSlug), {
    method: PUBLIC_ROUTES.reservationCreate.method,
    body: payload,
    marketKey,
    signal,
    // Fara referinta si fara stare, „rezervarea s-a facut" e o afirmatie pe care
    // clientul nu o poate verifica la restaurant.
    expect: (value) =>
      isRecord(value) && typeof value.reference === "string" && typeof value.status === "string",
  });
}

// ---------------------------------------------------------------------------
// Consimtamant
// ---------------------------------------------------------------------------

export interface ConsentRecordPayload {
  anonymousId: string;
  textVersion: string;
  decisions: Array<{ purpose: "analytics" | "marketing"; granted: boolean; textSnapshot: string }>;
}

/**
 * Trimite decizia spre jurnal.
 *
 * Deliberat FARA `expect`: raspunsul nu intereseaza pe nimeni. Decizia s-a
 * aplicat deja in browser inainte de apelul asta, iar apelantul nu trebuie sa
 * astepte nimic de la server ca sa o respecte. Trimiterea e pentru dovada, nu
 * pentru efect.
 */
export function recordConsent(
  marketKey: string,
  payload: ConsentRecordPayload,
  signal?: AbortSignal,
): Promise<unknown> {
  return request<unknown>(publicPath.consent(), {
    method: PUBLIC_ROUTES.consent.method,
    body: payload,
    marketKey,
    signal,
  });
}

// ---------------------------------------------------------------------------
// Geocodare
// ---------------------------------------------------------------------------

/**
 * Tipurile si calea vin acum din contract (defect A1/G3). A doua sursa de
 * coordonate — geolocatia browserului — nu depinde de server si ramane
 * disponibila cand geocodarea nu raspunde.
 */
export function geocodeAddress(
  marketKey: string,
  payload: GeocodeRequest,
  signal?: AbortSignal,
): Promise<GeocodeResponse> {
  return request<GeocodeResponse>(publicPath.geocode(), {
    method: PUBLIC_ROUTES.geocode.method,
    body: payload,
    marketKey,
    signal,
    // Lista goala inseamna "nicio potrivire". Lipsa listei inseamna alt raspuns.
    expect: (value) => isRecord(value) && Array.isArray(value.candidates),
  });
}

/**
 * Geocodare inversa: din punctul dat de browser in strada si localitate.
 *
 * Optionala in contract: pe un core mai vechi raspunde 404, iar apelantul cade
 * pe formularul completat de mana, cu coordonatele deja stiute.
 */
export function reverseGeocode(
  marketKey: string,
  payload: ReverseGeocodeRequest,
  signal?: AbortSignal,
): Promise<GeocodeResponse> {
  return request<GeocodeResponse>(publicPath.geocodeReverse(), {
    method: PUBLIC_ROUTES.geocodeReverse.method,
    body: payload,
    marketKey,
    signal,
    expect: (value) => isRecord(value) && Array.isArray(value.candidates),
  });
}

/**
 * Geolocatia browserului, ca text de eroare in romana. Nu inlocuieste adresa
 * scrisa: da doar coordonatele, restul campurilor raman ale clientului.
 */
export function locateWithBrowser(timeoutMs = 12_000): Promise<{ lat: number; lng: number }> {
  return new Promise((resolve, reject) => {
    if (typeof navigator.geolocation === "undefined") {
      reject(new Error("Browserul nu oferă acces la locație."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      (error) => {
        const reason =
          error.code === error.PERMISSION_DENIED
            ? "Accesul la locație a fost refuzat."
            : error.code === error.TIMEOUT
              ? "Locația nu a fost determinată în timpul alocat."
              : "Locația nu a putut fi determinată.";
        reject(new Error(reason));
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 },
    );
  });
}
